import React, { useCallback, useEffect, useMemo, useState } from 'react'
import DataGrid, { Column, Editing, Lookup } from 'devextreme-react/data-grid'
import axios from 'axios'
import notify from '../../utils/notify'
import { deleteData, editData, insertData, lookupStore } from '../../utils/model-data'

type PopupListField = {
	field: string
	title?: string
}

type ModelPopupArgs = {
	folder?: string
	model: string
	title?: string
	fields?: PopupListField[]
}

type ModelPopupProps = { args: ModelPopupArgs }
type Row = { ID: number | string; value: string }

const defaultFields: PopupListField[] = [
	{ field: 'Third Party Owner', title: 'Third Party Owners' },
	{ field: 'Network Type', title: 'Network Types' },
]

const ModelPopup: React.FC<ModelPopupProps> = ({ args }) => {
	const { folder = '', model } = args
	const fields = useMemo(() => (args.fields && args.fields.length > 0 ? args.fields : defaultFields), [args.fields])
	const [rowsByField, setRowsByField] = useState<Record<string, Row[]>>({})
	const [lookupsByField, setLookupsByField] = useState<Record<string, any>>({})
	const [loading, setLoading] = useState(false)
	const [allowInserting, setAllowInserting] = useState(true)
	const [allowUpdating, setAllowUpdating] = useState(true)
	const [allowDeleting, setAllowDeleting] = useState(true)

	const loadLookups = useCallback(async () => {
		try {
			const response = await axios.get(`${API_FIBER_URL}/view/fields?folder=${folder}&model=${model}`)
			const data = response?.data?.data || {}
			setLookupsByField(data.lookups || {})
			if ('inserting' in data) {
				setAllowInserting(Boolean(data.inserting))
			}
			if ('updating' in data) {
				setAllowUpdating(Boolean(data.updating))
			}
			if ('deleting' in data) {
				setAllowDeleting(Boolean(data.deleting))
			}
		} catch (error: any) {
			notify(error?.response?.data?.message || 'Error loading popup lookups', 'error')
		}
	}, [folder, model])

	const loadRows = useCallback(async () => {
		setLoading(true)
		try {
			const body: any = { skip: 0, take: 5000, model }
			if (folder) {
				body.folder = folder
			}
			const response = await axios.post(`${API_FIBER_URL}/view/data`, body)
			const data = Array.isArray(response.data?.data) ? response.data.data : []
			const next: Record<string, Row[]> = {}

			fields.forEach(({ field }) => {
				const uniqueByValue = new Map<string, Row>()
				data.forEach((r: any) => {
					if (r[field] === null || String(r[field]).trim() === '') {
						return
					}
					const value = String(r[field]).trim()
					if (!uniqueByValue.has(value)) {
						uniqueByValue.set(value, { ID: r.ID, value })
					}
				})
				next[field] = Array.from(uniqueByValue.values())
			})
			setRowsByField(next)
		} catch (error: any) {
			notify(error?.response?.data?.message || 'Error loading popup data', 'error')
		} finally {
			setLoading(false)
		}
	}, [fields, folder, model])

	useEffect(() => {
		loadRows()
		loadLookups()
	}, [loadLookups, loadRows])

	const onRowInserted = async (field: string, e: any) => {
		if (!allowInserting) return
		const value = String(e.data?.value || '').trim()
		if (!value) return
		try {
			await insertData('view', {
				values: { [field]: value },
				folder,
				model,
			})
			notify('Value added successfully', 'success')
			loadRows()
		} catch (error: any) {
			notify(error || 'Insert failed', 'error')
			await loadRows()
		}
	}

	const onRowUpdated = async (field: string, e: any) => {
		if (!allowUpdating) return
		const key = e.key
		const value = String(e.data?.value ?? e.newData?.value ?? '').trim()
		if (!value || key == null) return
		try {
			await editData('view', {
				key,
				values: { [field]: value },
				folder,
				model,
			})
			notify('Value updated successfully', 'success')
			loadRows()
		} catch (error: any) {
			notify(error || 'Update failed', 'error')
			await loadRows()
		}
	}

	const onRowRemoved = async (e: any) => {
		if (!allowDeleting) return
		const key = e.key
		if (key == null) return
		try {
			await deleteData('view', {
				key,
				folder,
				model,
			})
			notify('Value deleted successfully', 'success')
			loadRows()
		} catch (error: any) {
			notify(error || 'Delete failed', 'error')
			await loadRows()
		}
	}

	return (
		<div style={{ width: 'auto', height: '100%' }}>
			<div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
				{args.title ? <p style={{ fontSize: 16, fontWeight: 'bold', margin: 0 }}>{args.title}</p> : null}
				<div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0 }}>
					{fields.map(({ field, title: columnTitle }) => (
						<div key={field} style={{ flex: 1 }}>
							<DataGrid
								dataSource={rowsByField[field] || []}
								keyExpr="ID"
								showBorders={true}
								columnAutoWidth={true}
								height="calc(100% - 8px)"
								loadPanel={{ enabled: loading }}
								onRowInserted={e => onRowInserted(field, e)}
								onRowUpdated={e => onRowUpdated(field, e)}
								onRowRemoved={onRowRemoved}
							>
								<Editing
									mode="row"
									allowAdding={allowInserting}
									allowUpdating={allowUpdating}
									allowDeleting={allowDeleting}
									useIcons={true}
								/>
								<Column dataField="value" caption={columnTitle ?? field} width={'100%'}>
									{lookupsByField[field] && (
										<Lookup
											dataSource={lookupStore(lookupsByField[field])}
											valueExpr="value"
											displayExpr="text"
										/>
									)}
								</Column>
							</DataGrid>
						</div>
					))}
				</div>
			</div>
		</div>
	)
}

export default ModelPopup
